import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ActivityType } from '~/api/common/types';

import { useUser } from '~/api/user';
import { Text } from '~/components';
import { Field, Select } from '~/components/Form';
import { srOnlyStyle } from '~/styles/utilities.css';
import { clsx } from '~/styles/utils';
import { insertIf } from '~/utils/arrays';
import { isItDeveloper } from '~/utils/user';

import { useFormWithLinesContext } from '../../FormLines.context';
import { useSectionContext } from '../../Section/Section.context';
import { useLineContext } from '../../SectionLines/SectionLines.context';
import { descriptionStyle, labelStyle } from './FormActivityType.css';

export const FormActivityType = () => {
  const { control } = useFormWithLinesContext();
  const { isMobile, isProductLine, lineFieldName } = useLineContext();
  const { section } = useSectionContext();

  const { data: user } = useUser();
  const { t } = useTranslation(['lines', 'validation']);

  const items = (
    [
      'CreativeGeneral',
      ...insertIf(isItDeveloper(user) && !section.isPhotoAlbum, 'CreativeComputer'),
      'NonCreative',
      'CreativeReuse',
      'CreativeSubcontracting',
    ] satisfies ActivityType[]
  ).map((activityType) => ({
    label: t(`lines:creativeWork.options.${activityType}.label`),
    description: t(`lines:creativeWork.options.${activityType}.description`),
    value: activityType,
  }));

  return (
    <Controller
      control={control}
      name={`${lineFieldName}.activityType`}
      render={({ field: { name, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
        <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
          <Field.Label className={clsx(!isMobile && srOnlyStyle)}>{t('lines:creativeWork.header')}</Field.Label>

          <Select.Root disabled={isProductLine} items={items} onValueChange={(next) => next && onChange(next)} value={value}>
            <Select.Button placeholder={t('lines:creativeWork.placeholder')} ref={ref} />
            <Select.List>
              {items.map((item) => (
                <Select.Item key={item.value} value={item.value}>
                  <Text className={labelStyle}>{item.label}</Text>
                  <Text className={descriptionStyle}>{item.description}</Text>
                </Select.Item>
              ))}
            </Select.List>
          </Select.Root>

          {isMobile && <Field.Error match={!!error}>{error?.message}</Field.Error>}
        </Field.Root>
      )}
      rules={{ required: { value: true, message: t('validation:required') } }}
    />
  );
};
